import { motion } from "framer-motion";
import { Flame, Hammer, Hand, History } from "lucide-react";
import { IMAGES } from "../lib/constants";

const VALUES = [
  { icon: Hammer, title: "Hand-Distressed Finishes", text: "Aged paints, worn edges and timeworn patinas — every piece a step back in time." },
  { icon: Hand, title: "Carefully Curated", text: "Nearly everything is gathered and curated by the owner — all in keeping with the country cozy." },
  { icon: History, title: "Honest Reproduction", text: "Faithful to the old patterns and methods, so new pieces feel like heirlooms." },
  { icon: Flame, title: "Warm Primitive Light", text: "Candlelight first. The shop glows the way a country home should." },
];

const TIMELINE = [
  { year: "2018", title: "The crow lands in Mississippi Mills", text: "The Olde Crow Boutique first opens its doors in Mississippi Mills, Ontario — a little shop of country goods, primitive folk craft and gifts." },
  { year: "2023", title: "Home to Nova Scotia", text: "The owner returns home to Nova Scotia, and the crow comes too — roosting at The Sunnyside Mall on the Bedford Highway, three happy years and counting." },
  { year: "Today", title: "One shop, all the cozy", text: "The Kilted Goat Country Store has blended into The Olde Crow — one shop now, carefully gathered and curated in keeping with the country cozy." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <section className="grain-overlay bg-[#111110] py-20 sm:py-24">
        <div className="crow-container">
          <p className="font-hand text-2xl text-[#C27803]">about our shop</p>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl font-semibold leading-tight tracking-tight text-[#FDFBF7] sm:text-5xl">
            A little shop with old soul, in Bedford, Nova Scotia
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#E8DFC8]">
            The Olde Crow Boutique first opened its doors in 2018 in Mississippi Mills, Ontario, with a simple belief:
            a home should be filled with things that mean something. When the owner returned home to Nova Scotia, the
            crow came too — roosting at The Sunnyside Mall in Bedford for the last three years.
          </p>
        </div>
      </section>

      <motion.section
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
        className="crow-container grid items-center gap-12 py-20 sm:py-24 lg:grid-cols-2"
      >
        <div className="relative">
          <img src="/storefront-shop.jpeg" alt="The Olde Crow Boutique storefront at Sunnyside Mall" className="w-full rounded-md border border-[#D8CDBC] object-cover shadow-md" />
          <p className="absolute -bottom-5 right-6 rotate-2 rounded-sm bg-[#E8DFC8] px-4 py-2 font-hand text-xl text-[#292725] shadow-md">
            "as warm as a hug", our customers say
          </p>
        </div>
        <div>
          <p className="eyebrow-label">Our Philosophy</p>
          <h2 className="mt-3 font-serif text-2xl font-semibold tracking-tight text-[#292725] sm:text-3xl">
            Nothing fast. Nothing fussy. Everything with a story.
          </h2>
          <div className="mt-6 space-y-4 text-base leading-relaxed text-[#5C554B]">
            <p>
              We believe the best rooms are gathered, not decorated. A crock that held grandmother's pickles. A star
              that watched over a barn for fifty years. A candle poured by hands you could shake.
            </p>
            <p>
              That is what we stock the shop with — honest country goods, primitive folk craft, and gifts that feel
              like they were always meant for the person receiving them. Nearly everything is carefully gathered and
              curated by the owner, in keeping with the country cozy — with a few handmade treasures mixed in.
            </p>
          </div>
        </div>
      </motion.section>

      <section className="bg-[#F4EFE6] py-20 sm:py-24" data-testid="values-section">
        <div className="crow-container">
          <p className="eyebrow-label">What We Stand By</p>
          <h2 className="mt-3 font-serif text-2xl font-semibold tracking-tight text-[#292725] sm:text-3xl">Our crafting values</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v) => (
              <div key={v.title} className="rounded-md border border-[#D8CDBC] bg-[#FDFBF7] p-6 shadow-sm">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-[#C27803]/15 text-[#C27803]">
                  <v.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 font-serif text-lg font-semibold text-[#292725]">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#8A7E70]">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="crow-container grid gap-12 py-20 sm:py-24 lg:grid-cols-2" data-testid="timeline-section">
        <div>
          <p className="eyebrow-label">The Journey So Far</p>
          <h2 className="mt-3 font-serif text-2xl font-semibold tracking-tight text-[#292725] sm:text-3xl">
            From Mississippi Mills to Sunnyside Mall
          </h2>
          <ol className="mt-10 space-y-8 border-l-2 border-dashed border-[#D8CDBC] pl-8">
            {TIMELINE.map((t) => (
              <li key={t.year} className="relative">
                <span className="absolute -left-[41px] flex h-5 w-5 items-center justify-center rounded-full border-2 border-[#C27803] bg-[#FDFBF7]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#C27803]" />
                </span>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#8C251C]">{t.year}</p>
                <h3 className="mt-1 font-serif text-xl font-semibold text-[#292725]">{t.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-[#8A7E70]">{t.text}</p>
              </li>
            ))}
          </ol>
          <div className="relative mt-12 max-w-xs rotate-[-2deg]" data-testid="crow-sentinel">
            <img src="/crow-portrait.jpeg" alt="The crow himself, keeping watch" loading="lazy" className="w-full rounded-md border-4 border-[#D8CDBC] shadow-lg" />
            <p className="mt-3 text-center font-hand text-xl text-[#8A7E70]">the fence-post sentinel, still on duty</p>
          </div>
        </div>
        <div className="rounded-md border border-[#D8CDBC] bg-[#F4EFE6] p-8 sm:p-10" data-testid="shopkeeper-card">
          <p className="eyebrow-label">Meet the Shopkeeper</p>
          <img src="/kimberley-horses.jpeg" alt="Kimberley with her horses at the farm" className="mt-6 h-64 w-full rounded-md border border-[#D8CDBC] object-cover" />
          <h3 className="mt-6 font-serif text-2xl font-semibold text-[#292725]">Kimberley Irvine</h3>
          <p className="mt-1 font-hand text-xl text-[#8C251C]">owner, gatherer &amp; keeper of the crow</p>
          <p className="mt-4 text-sm leading-relaxed text-[#5C554B]">
            Kimberley opened The Olde Crow in 2018 and gathers every treasure on the shelves herself, with an amazing
            team beside her. When she's not at the shop, she's at her equestrian farm with its many characters —
            horses, donkeys, and a pet pig named Hamlet. Stop in and say hello — you'll likely leave with a wreath,
            and a story.
          </p>
        </div>
      </section>

      <section className="crow-container py-20 sm:py-24" data-testid="team-section">
        <p className="eyebrow-label">The Amazing Team</p>
        <h2 className="mt-3 font-serif text-2xl font-semibold tracking-tight text-[#292725] sm:text-3xl">
          The faces behind the counter
        </h2>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { name: "Tara", role: "Manager", note: "Neighbour farmer and best friend — she can chat with you about goats… lots of goats. And horses." },
            { name: "Marquis", role: "Shop Keeper & Farm Manager", note: "Kimberley's niece and an equestrian — at home behind the counter or in the saddle." },
            { name: "Stephanie", role: "Shop Keeper", note: "Our staff sailor — she keeps the shop shipshape and the welcome warm." },
            { name: "Barb", role: "Shop Keeper & Volunteer", note: "Our newest team member, with the very best job of all — grandmother. xx" },
          ].map((m) => (
            <div key={m.name} data-testid={`team-card-${m.name.toLowerCase()}`} className="rounded-md border border-[#D8CDBC] bg-[#F4EFE6] p-6 shadow-sm">
              <span className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#9A5E02] bg-[#C27803] font-serif text-xl font-bold text-[#FDFBF7]">
                {m.name[0]}
              </span>
              <h3 className="mt-4 font-serif text-xl font-semibold text-[#292725]">{m.name}</h3>
              <p className="mt-0.5 font-hand text-lg text-[#8C251C]">{m.role}</p>
              <p className="mt-2 text-sm leading-relaxed text-[#8A7E70]">{m.note}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="grain-overlay bg-[#111110] py-20 sm:py-24" data-testid="farm-family-section">
        <div className="crow-container">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#E8873A]">The Farm Family</p>
          <h2 className="mt-3 font-serif text-2xl font-semibold tracking-tight text-[#FDFBF7] sm:text-3xl">
            Kimberley's other full-time job
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#E8DFC8]">
            Home from the shop is an equestrian farm full of characters — horses who know the sound of the treat bag,
            donkeys with opinions, and a pet pig named Hamlet who firmly believes he runs the place.
          </p>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            <div className="overflow-hidden rounded-md border border-[#3A3632] bg-[#1A1918]" data-testid="farm-card-herd">
              <img src="/kimberley-horses.jpeg" alt="The herd at the farm" loading="lazy" className="h-56 w-full object-cover" />
              <div className="p-5">
                <h3 className="font-serif text-xl font-semibold text-[#FDFBF7]">The Herd</h3>
                <p className="mt-1 font-hand text-lg text-[#E8873A]">horses &amp; donkeys — every one a character</p>
              </div>
            </div>
            <div className="overflow-hidden rounded-md border border-[#3A3632] bg-[#1A1918]" data-testid="farm-card-hamlet">
              <img src="/hamlet-piglet.jpeg" alt="Hamlet the pig as a piglet, snuggled in a plaid blanket" loading="lazy" className="h-56 w-full object-cover" />
              <div className="p-5">
                <h3 className="font-serif text-xl font-semibold text-[#FDFBF7]">Hamlet</h3>
                <p className="mt-1 font-hand text-lg text-[#E8873A]">pet pig &amp; self-appointed farm foreman</p>
              </div>
            </div>
            <div className="overflow-hidden rounded-md border border-[#3A3632] bg-[#1A1918]" data-testid="farm-card-crow">
              <img src="/crow-portrait.jpeg" alt="The crow keeping watch over the farm" loading="lazy" className="h-56 w-full object-cover" />
              <div className="p-5">
                <h3 className="font-serif text-xl font-semibold text-[#FDFBF7]">The Crow</h3>
                <p className="mt-1 font-hand text-lg text-[#E8873A]">fence-post sentinel, namesake &amp; overseer</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
